import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, FileText, User, Calendar, Filter, X } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { formatDateTime } from '@/lib/utils';
import { ModuleTag, type Document as EgpaDocument, type RecordItem, type Prisoner } from '@/types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface SearchResult extends Omit<RecordItem, 'documents'> {
  prisoner: Pick<Prisoner, 'id' | 'prisonerNumber' | 'firstName' | 'lastName'>;
  documents: Array<Pick<EgpaDocument, 'id' | 'filename' | 'mimeType'>>;
  criminalCase?: { romanNumeral: string; caseReference?: string };
}

interface SearchResponse {
  data: SearchResult[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const moduleLabels: Record<ModuleTag, string> = {
  [ModuleTag.VOLLZUGSVERLAUF]: 'Vollzugsverlauf',
  [ModuleTag.VOLLZUGSPLANUNG]: 'Vollzugsplanung',
  [ModuleTag.DISZIPLINAR]: 'Disziplinarmaßnahmen',
  [ModuleTag.LOCKERUNGEN]: 'Lockerungen/Ausgänge',
  [ModuleTag.BESUCHE]: 'Besuche/Kontakte',
  [ModuleTag.DOKUMENTE]: 'Dokumente',
  [ModuleTag.NOTIZEN]: 'Notizen/Vermerke',
  [ModuleTag.VOLLSTRECKUNG]: 'Vollstreckung',
};

const needleLabels: Record<number, string> = {
  1: 'Nadel 1 - Grundunterlagen',
  2: 'Nadel 2 - Vollstreckung',
  3: 'Nadel 3 - Sonstige',
};

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [submittedQuery, setSubmittedQuery] = useState(initialQuery);
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [selectedNeedle, setSelectedNeedle] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  // Update when URL query changes
  useEffect(() => {
    const q = searchParams.get('q') || '';
    if (q && q !== submittedQuery) {
      setSearchQuery(q);
      setSubmittedQuery(q);
    }
  }, [searchParams]);

  const { data: results, isLoading, error } = useQuery({
    queryKey: ['search', submittedQuery, selectedModule, selectedNeedle, dateFrom, dateTo, page],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          query: submittedQuery,
          modules: selectedModule ? [selectedModule] : undefined,
          needle: selectedNeedle ? parseInt(selectedNeedle) : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          page,
          pageSize: 20,
        }),
      });

      if (!response.ok) {
        throw new Error('Suche fehlgeschlagen');
      }

      return response.json() as Promise<SearchResponse>;
    },
    enabled: submittedQuery.length > 0,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSubmittedQuery(searchQuery);
  };

  const clearFilters = () => {
    setSelectedModule('');
    setSelectedNeedle('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
    if (submittedQuery) {
      setSubmittedQuery(searchQuery);
    }
  };

  const getNeedleColor = (needle: number) => {
    switch (needle) {
      case 1: return 'bg-blue-500';
      case 2: return 'bg-green-500';
      case 3: return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Suche</h1>
        <p className="text-muted-foreground">
          Durchsuchen Sie Akteneinträge und Dokumente nach Schlagworten
        </p>
      </div>

      {/* Search Form */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Suchbegriff eingeben..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit" disabled={!searchQuery.trim()}>
                <Search className="mr-2 h-4 w-4" />
                Suchen
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                Filter
              </Button>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="grid gap-4 rounded-lg border p-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label>Modul</Label>
                  <Select value={selectedModule} onValueChange={setSelectedModule}>
                    <SelectTrigger>
                      <SelectValue placeholder="Alle Module" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Alle Module</SelectItem>
                      {Object.entries(moduleLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Nadel</Label>
                  <Select value={selectedNeedle} onValueChange={setSelectedNeedle}>
                    <SelectTrigger>
                      <SelectValue placeholder="Alle Nadeln" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Alle Nadeln</SelectItem>
                      {Object.entries(needleLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Datum von</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Datum bis</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>

                <div className="md:col-span-4">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Filter zurücksetzen
                  </Button>
                </div>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      {isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Suche läuft...</p>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card>
          <CardContent className="py-12 text-center text-red-500">
            Fehler bei der Suche: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {results && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Suchergebnisse</span>
                <span className="text-sm font-normal text-muted-foreground">
                  {results.total} Treffer gefunden
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {results.data.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  Keine Ergebnisse für "{submittedQuery}" gefunden.
                </p>
              ) : (
                <div className="space-y-4">
                  {results.data.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-lg border p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span
                              className={`h-3 w-3 rounded-full ${getNeedleColor(item.needle)}`}
                            />
                            <Link
                              to={`/prisoners/${item.prisoner.id}`}
                              className="text-lg font-semibold hover:underline"
                            >
                              {item.title}
                            </Link>
                          </div>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              <Link
                                to={`/prisoners/${item.prisoner.id}`}
                                className="hover:underline"
                              >
                                {item.prisoner.lastName}, {item.prisoner.firstName}
                              </Link>
                              <span className="font-mono">
                                ({item.prisoner.prisonerNumber})
                              </span>
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatDateTime(item.createdAt)}
                            </span>
                            <span>{needleLabels[item.needle]}</span>
                            {item.criminalCase && (
                              <span className="font-mono">
                                Haftsache {item.criminalCase.romanNumeral}
                              </span>
                            )}
                          </div>

                          {/* Module Tags */}
                          <div className="flex flex-wrap gap-1 mb-3">
                            {item.modules.map((m) => (
                              <span
                                key={m}
                                className="rounded bg-muted px-2 py-0.5 text-xs"
                              >
                                {moduleLabels[m as ModuleTag] || m}
                              </span>
                            ))}
                          </div>

                          {/* Documents */}
                          {item.documents.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {item.documents.map((doc) => (
                                <span
                                  key={doc.id}
                                  className="flex items-center gap-1 rounded border px-2 py-1 text-xs"
                                >
                                  <FileText className="h-3 w-3" />
                                  {doc.filename}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <Link to={`/prisoners/${item.prisoner.id}`}>
                          <Button variant="outline" size="sm">
                            Zur Akte
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {results.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Zurück
              </Button>
              <span className="text-sm text-muted-foreground">
                Seite {page} von {results.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(results.totalPages, p + 1))}
                disabled={page >= results.totalPages}
              >
                Weiter
              </Button>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!submittedQuery && !isLoading && (
        <Card>
          <CardContent className="py-16 text-center">
            <Search className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Dokumente durchsuchen</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Geben Sie einen Suchbegriff ein, um Akteneinträge, Dokumente und
              OCR-extrahierte Texte zu durchsuchen. Nutzen Sie die Filter für
              eine gezieltere Suche.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
